import { Close } from "@mui/icons-material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { Box, Button, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import Table, { ColumnType } from "components/Table";
import { snack } from "components/toast";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResType } from "types";
import { getCurrentFormattedDateTime, getTitle } from "utils";
import { SortDirection } from "data/constants";
import sortHandler from "components/Table/SortHandler";
import ClientFilter from "views/billing/expenditure/Filter";
import { selectExpenditure, removeIndexFilter } from "redux/reducers/expenditureSlice";
import { useDispatch, useSelector } from "react-redux";
import { StyledAppliedFilterItem } from "views/tasks/Filters/styles";
import useTitle from "hooks/useTitle";
import { formattedAmount } from "utils/formattedAmount";
import { exportGstTdsBilling, getGstTdsTotal, getInvoices, getInvoicesGstPayable, updateGSTTdsStatus } from "api/services/billing/invoices";
import { formattedDate } from "utils/formattedDate";
import { mulitipleClient, primaryclient } from "assets";
import { getSavedGstTds } from "views/billing/invoices/calculations";
import InvoicesHeader from "views/billing/invoices/InvoicesHeader";
import PageWithPermission from "components/PageWithPermission";
import { Permissions } from "data/permissons";
import { handleError } from "utils/handleError";
import { usePermissions } from "context/PermissionsProvider";
import AccessDenied from "../invoices/AccressDenied";
import AmountFormat from "components/AmountFormat";
import moment from "moment";
import { getStatusColor } from "views/billing/invoices/getStatusColor";

function GstReceivable(state) {
    useTitle("Billing | GST Payable");
    const { permissions } = usePermissions();
    const [sort, setSort] = useState<any>({});
    const [columns, setColumns] = useState(column);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const states = useSelector(selectExpenditure);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number>(10);
    const [dates, setDates] = useState({ fromDate: null, toDate: null });
    const [filters, setFilters] = useState<any>({ status: [], billingEntity: [], type: [] });
    const [selected, setSelected] = useState<any[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const { data, isLoading }: ResType = useQuery(
        ["invoices", {
            offset: page * pageCount,
            limit: pageCount,
            search,
            status: filters.status,
            billingEntity: filters.billingEntity,
            type: filters.type,
            ...dates,
            fromDate: dates.fromDate ? moment(dates.fromDate).utcOffset("+05:30", true).format("YYYY-MM-DD") : null,
            toDate: dates.toDate ? moment(dates.toDate).utcOffset("+05:30", true).format("YYYY-MM-DD") : null,
            sort,
            screen: 'gsttds'

        }],
        getInvoices, {
        onError: (err) => {
            if (!err?.error) {
                snack.error(`An error occurred while fetching invoices`);
            }
        },
        enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)
    }
    );


    const { data: gstTdsData }: ResType = useQuery(
        ['gst-tds-total', {
            search,
            status: filters.status,
            billingEntity: filters.billingEntity,
            fromDate: dates.fromDate ? moment(dates.fromDate).utcOffset("+05:30", true).format("YYYY-MM-DD") : null,
            toDate: dates.toDate ? moment(dates.toDate).utcOffset("+05:30", true).format("YYYY-MM-DD") : null,
        }],
        getGstTdsTotal, {
        onError: (err) => {
            if (!err?.error) {
                snack.error(`An error occurred while fetching GST TDS totals`);
            }
        },
        enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)

    }

    );

    const gstTdsTotals = gstTdsData?.data

    const setSearchChange = (e) => {
        setSearch(e);
        setPage(0);
    };
    const handleDates = (value) => {
        setDates(value);
        setPage(0);
    }
    const handleSort = (v: any) => {
        sortHandler({
            key: v.key,
            columns,
            sortState: sort,
            setSortState: setSort,
            setColumns: setColumns,
        });
    };

    const onRemoveFilter = (filterKey: any, filterItemIndex: any) => {
        dispatch(removeIndexFilter({ filterKey, filterItemIndex }));

    };

    const handleRowClick = (v: any) => {
        window.open(`/billing/${v?.creditNoteNumber ? "credit-note" : "invoices"}/${v?.id}/preview`);
    };
    const { mutate: mutate1, isLoading: isExporting } = useMutation(exportGstTdsBilling, {
        onSuccess: (res: any) => {
            try {
                const arr = new Uint8Array(res.data?.data);
                const blob = new Blob([arr], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const file = window.URL.createObjectURL(blob);
                const formattedDateTime = getCurrentFormattedDateTime();

                const link = document.createElement("a");
                link.href = file;
                link.download = `ATOM - GST TDS Receivable ${formattedDateTime}.xlsx`;
                link.click();

                snack.success("Exported Successfully");
            } catch {
                snack.error("Invalid Excel file response");
            }
        },
        onError: (err: any) => {
            snack.error(handleError(err));
        },
    });

    const handleExport = () => {
        if (isExporting) return;
        // Same filters as the table; the backend sets screen 'gsttds' and drops pagination
        mutate1({
            search,
            status: filters.status,
            billingEntity: filters.billingEntity,
            fromDate: dates.fromDate ? moment(dates.fromDate).utcOffset("+05:30", true).format("YYYY-MM-DD") : null,
            toDate: dates.toDate ? moment(dates.toDate).utcOffset("+05:30", true).format("YYYY-MM-DD") : null,
            sort,
        });
    };

    const { mutate: updateTDS } = useMutation(updateGSTTdsStatus, {
        onSuccess: (res: any) => {
        snack.success("TDS Credit status updated for the selected Invoices");
        setSelected([]);
        queryClient.invalidateQueries("invoices");
        },
        onError: (err: any) => {
        snack.error(handleError(err));
        },
    });

    const handleGSTTDSReceivableYes = () => {
        updateTDS({ selected: selected, gstTdsStatus: "RECEIVED" })
    }

    const handleGSTTDSReceivableNo = () => {
        updateTDS({ selected: selected, gstTdsStatus: "NOTRECEIVED" })
    }

    const totalCount = data?.data?.totalCount || 0;


    if (!(permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS))) {
        return <AccessDenied />
    }


    return (
        <> <PageWithPermission name={Permissions.GST_PAYABLE}>
            <Box p={2} pl={1} pr={1} pb={0}>
                    {selected.length > 0 && (
                        <Box ml="auto">
                            <Button
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                variant="outlined"
                                color="secondary"
                                endIcon={<KeyboardArrowDownOutlinedIcon />}
                            >
                                Actions
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                            >
                                <MenuItem onClick={() => handleGSTTDSReceivableYes()}>Received</MenuItem>
                                <MenuItem onClick={() => handleGSTTDSReceivableNo()}>Not Received</MenuItem>
                            </Menu>
                        </Box>
                    )}
                <InvoicesHeader
                    search={search}
                    setSearch={setSearchChange}
                    filters={filters}
                    setFilters={setFilters}
                    setPage={setPage}
                    dates={dates}
                    setDates={handleDates}
                    sort={sort}
                    gstTds={true}
                    handleExport={handleExport}
                    isExporting={isExporting}
                />
                <Box display='flex' alignItems='space-between' mt={1}>
                    {Object.keys(states?.appliedFilters).map((filter, filterIndex: number) => {
                        if (states?.appliedFilters[filter].length > 0) {
                            return (
                                <Box key={filterIndex} ml={2} >
                                    <Box display='flex' gap={1} alignItems="center">

                                        <Typography variant="caption">{getTitle(filter)}:</Typography>

                                        <Box display='flex'>
                                            {states?.appliedFilters[filter].map(
                                                (item: any, filterItemIndex: number) => (
                                                    <StyledAppliedFilterItem
                                                        onClick={() => {
                                                            onRemoveFilter(filter, filterItemIndex);
                                                        }}
                                                        key={filterItemIndex}
                                                    >
                                                        <Typography variant="body2">
                                                            {item?.name || item?.fullName}
                                                        </Typography>
                                                        <Close sx={{ fontSize: "11px" }} />
                                                    </StyledAppliedFilterItem>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }
                        return null;
                    })}
                </Box>

            </Box >
            <Box pl={1} pr={1} pb={0}>
                <Table
                    sx={{ mt: 1, pb: 0, mb: 0, height: '495px' }}
                    data={data?.data?.result || []}
                    pagination={{ totalCount, page, setPage, pageCount, setPageCount }}
                    loading={isLoading}
                    columns={columns}
                    sortHandler={handleSort}
                    // onRowClick={handleRowClick}
                    selection={{ selected, setSelected }}
                    columnTotals={{
                        columns: [
                            "subTotal",
                            "igst",
                            "cgst",
                            "sgst",
                        ],
                        totals: gstTdsTotals
                    }}

                />
            </Box>
        </PageWithPermission>
            <ClientFilter
                open={openFilter}
                setOpen={setOpenFilter}
            />
        </>
    );
}

// GST TDS (Sec 51) for a row, same rule as the invoice/credit note preview:
// inter-state (or divideTax) -> IGST 2%, intra-state -> CGST 1% + SGST 1%, on the taxable value
// Invoice taxable value less the taxable value of all its (non-cancelled) credit notes
const getNetTaxableValue = (row: any) => (Number(row?.subTotal) || 0) - (Number(row?.creditNoteSubTotal) || 0);

const getRowGstTds = (row: any) => {
    if (!(row?.hasGstTds && row?.billingEntity?.hasGst)) {
        return { igst: 0, cgst: 0, sgst: 0 };
    }
    const sameState = row?.billingEntity?.locationOfSupply === row?.placeOfSupply?.split('-')[1];
    const isIgst = !sameState || !!row?.divideTax;
    const { igst, cgst, sgst } = getSavedGstTds({ ...row, subTotal: getNetTaxableValue(row) }, isIgst);
    return { igst, cgst, sgst };
};

const column: Array<ColumnType> = [
    {
        title: "Billing Entity",
        key: "",
        render: (row: any) => <Typography>{row?.billingEntity?.tradeName}</Typography>
    },
    // {
    //     title: "Type",
    //     key: "type",
    //     render: (row: any) => <Typography color={row?.creditNoteNumber ? '#E11D48' : 'text.primary'}>{row.invoiceNumber ? row.client?.gstVerified ? "INV (B2B)" : "INV (B2C)" : row?.creditNoteNumber ? row?.client?.gstVerified ? "CRN (B2B)" : "CRN (B2C)" : null}</Typography>
    // },
    {
        title: "Clients/Clients Group (GSTIN)",
        key: "client.displayName",
        render: (row: any) => (
            row.client ? (
                <Typography sx={{ display: "flex", alignItems: "center" }}>

                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <span>{row.client.displayName}</span>
                        {row.client.gstVerified && (
                            <span>({row.client.gstNumber})</span>
                        )}
                    </Box>

                    {row.client.isPrimary && (
                        <img
                            src={primaryclient}
                            width="22px"
                            style={{ marginLeft: "10px", marginRight: "10px" }}
                            alt="primary"
                        />
                    )}

                </Typography>
            ) : (
                <Typography sx={{ display: "flex", alignItems: "center" }}>

                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <span>{row.clientGroup?.displayName}</span>
                        {row.clientGroup?.gstVerified && (
                            <span>({row.clientGroup?.gstNumber})</span>
                        )}
                    </Box>

                    <img
                        src={mulitipleClient}
                        width="22px"
                        style={{ marginLeft: "10px", marginRight: "10px" }}
                        alt="group"
                    />

                </Typography>
            )
        )
    },
    {
        title: "Invoice",
        key: "invoiceNumber",
        render: (row: any) => <Typography>{row.invoiceNumber ? row.invoiceNumber : row?.creditNoteNumber}</Typography>
    },
    {
        key: "status",
        title: "Invoice Status",
        sort: true,
        active: false,
        direction: SortDirection.ASC,
        render: (row: any) => (
            <Box
                sx={{
                    background: getStatusColor(((row?.status == 'APPROVAL_PENDING') && (row?.invoiceDueDate <= moment().subtract(1, "day").format("YYYY-MM-DD"))) ? "OVERDUE" : (row?.status == 'PARTIALLY_PAID' && row?.invoiceDueDate <= moment().subtract(1, "day").format("YYYY-MM-DD")) ? "PARTIALLY_PAID_OVERDUE" : row?.status),
                    px: 2,
                    py: "4px",
                    color: "white",
                    borderRadius: "10px",
                    textAlign: "center",
                    display: "inline-flex",
                }}
            >
                <Typography variant="body2">
                    {row?.status == 'APPROVAL_PENDING' || row?.status == 'PARTIALLY_PAID' ?
                        (row?.invoiceDueDate > moment().subtract(1, 'day').format("YYYY-MM-DD") ?
                            (row?.status == 'APPROVAL_PENDING' ? getTitle('Created') :
                                getTitle(row?.status?.toLowerCase())) : (row?.status == 'APPROVAL_PENDING' ? 'Overdue' : 'Partially Paid - Overdue')) :
                        getTitle(row?.status?.toLowerCase())}
                </Typography>
            </Box>
        ),
    },
    {
        title: "Date",
        key: "invoiceDate",
        sort: true,
        active: false,
        direction: SortDirection.ASC,
        render: (row: any) => formattedDate(row.invoiceDate ? row.invoiceDate : row?.creditNoteDate),
    },



    {
        title: "Taxable Value",
        key: "subTotal",
        justifyContent: 'flex-end',
        sort: true,
        active: false,
        width: '1px',
        direction: SortDirection.ASC,
        render: (row: any) => <AmountFormat creditNote={!!row.creditNoteNumber} amount={formattedAmount(getNetTaxableValue(row))} />

    },
    {
        title: "IGST",
        key: "igst",
        justifyContent: 'flex-end',
        render: (row: any) => <AmountFormat creditNote={!!row.creditNoteNumber} amount={formattedAmount(getRowGstTds(row).igst)} />
    },
    {
        title: "CGST",
        key: "cgst",
        justifyContent: 'flex-end',
        render: (row: any) => <AmountFormat creditNote={!!row.creditNoteNumber} amount={formattedAmount(getRowGstTds(row).cgst)} />
    },
    {
        title: "S/UTGST",
        key: "sgst",
        justifyContent: 'flex-end',
        render: (row: any) => <AmountFormat creditNote={!!row.creditNoteNumber} amount={formattedAmount(getRowGstTds(row).sgst)} />
    },
    // {
    //     title: "Pure Agent",
    //     key: "totalCharges",
    //     justifyContent: 'right',
    //     sort: true,
    //     active: false,
    //     direction: SortDirection.ASC,
    //     render: (row: any) => <AmountFormat creditNote={!!row.creditNoteNumber} amount={formattedAmount(row?.totalCharges)} />
    // },
    // {

    //     key: "grandTotal",
    //     title: "Invoice Value",
    //     justifyContent: 'center',
    //     width: "5px",
    //     sort: true,
    //     active: false,
    //     direction: SortDirection.ASC,
    //     render: (row: any) => <AmountFormat creditNote={!!row.creditNoteNumber} amount={formattedAmount(row.grandTotal)} />
    // },
    {
        title: "Electronic Cash Ledger",
        key: "gstTdsStatus",
        sort: true,
        active: false,
        direction: SortDirection.ASC,
        render: (row: any) => row?.gstTdsStatus ? (
            <Box
                sx={{
                    background: getStatusColor(row?.gstTdsStatus),
                    px: 2,
                    py: "4px",
                    color: "white",
                    borderRadius: "10px",
                    textAlign: "center",
                    display: "inline-flex",
                }}
            >
                <Typography variant="body2">{getTitle(row.gstTdsStatus)}</Typography>
            </Box>
        ) : <Typography>-</Typography>
    },

];

export default GstReceivable;
